package com.evoapps.evoshift.domain.billing

import kotlin.time.Instant

/**
 * Billing facts shared by every platform (SH-38): what the store (RevenueCat) knows about the
 * owner, what can be bought, and the owner's resulting plan. The UI turns them into text.
 */

/** How often a plan renews. */
enum class BillingPeriod {
    WEEKLY, MONTHLY, QUARTERLY, SEMIANNUAL, YEARLY, LIFETIME, UNKNOWN;

    companion object {
        /** From an ISO 8601 period as RevenueCat reports it (`P1M`, `P1Y`, `P3M`, `P7D`...). */
        fun fromIso(iso: String?): BillingPeriod = when (BillingDuration.fromIso(iso)?.inMonthsOrWeeks()) {
            "W1" -> WEEKLY
            "M1" -> MONTHLY
            "M3" -> QUARTERLY
            "M6" -> SEMIANNUAL
            "M12" -> YEARLY
            else -> UNKNOWN
        }

        /**
         * From a RevenueCat package type: the SDK enum name (`MONTHLY`, `ANNUAL`) or the package
         * identifier (`$rc_monthly`, `$rc_annual`); null when it says nothing about the period.
         */
        fun fromPackageType(type: String?): BillingPeriod? = when (type?.trim()?.lowercase()?.removePrefix("\$rc_")) {
            "weekly" -> WEEKLY
            "monthly" -> MONTHLY
            "three_month" -> QUARTERLY
            "six_month" -> SEMIANNUAL
            "annual" -> YEARLY
            "lifetime" -> LIFETIME
            else -> null
        }
    }
}

enum class PeriodUnit { DAY, WEEK, MONTH, YEAR }

/** A length of time such as a free trial's ("7 days", "1 month"). */
data class BillingDuration(val value: Int, val unit: PeriodUnit) {
    internal fun inMonthsOrWeeks(): String? = when (unit) {
        PeriodUnit.DAY -> if (value % 7 == 0) "W${value / 7}" else null
        PeriodUnit.WEEK -> "W$value"
        PeriodUnit.MONTH -> "M$value"
        PeriodUnit.YEAR -> "M${value * 12}"
    }

    companion object {
        private val ISO = Regex("""^P(\d+)([DWMY])$""")

        fun fromIso(iso: String?): BillingDuration? {
            val match = ISO.matchEntire(iso?.trim()?.uppercase() ?: return null) ?: return null
            val value = match.groupValues[1].toIntOrNull() ?: return null
            return BillingDuration(value, unitOf(match.groupValues[2]) ?: return null)
        }

        /** A store SDK's unit name (`DAY`, `WEEK`, `MONTH`, `YEAR`, also `D`/`W`/`M`/`Y`). */
        fun of(value: Int, unit: String?): BillingDuration? =
            unitOf(unit?.trim()?.uppercase()?.take(1))?.let { BillingDuration(value, it) }

        private fun unitOf(letter: String?): PeriodUnit? = when (letter) {
            "D" -> PeriodUnit.DAY
            "W" -> PeriodUnit.WEEK
            "M" -> PeriodUnit.MONTH
            "Y" -> PeriodUnit.YEAR
            else -> null
        }
    }
}

/** A free trial or a discounted first period in front of the regular price. */
data class IntroductoryOffer(
    val isFreeTrial: Boolean,
    /** The store's formatted price of the intro period; null for a free trial. */
    val formattedPrice: String?,
    val duration: BillingDuration?
)

/** Something the paywall can sell: one package of the RevenueCat offering. */
data class PlanOffer(
    val packageId: String,
    val productId: String,
    val tier: PlanTier,
    val period: BillingPeriod,
    /** The store's localised price, e.g. "49,99 zł". */
    val formattedPrice: String,
    val introductoryOffer: IntroductoryOffer? = null
) {
    /** The seats this package sells, from its product (or package) id; see [Plans.seatsOf]. */
    val seats: Int get() = Plans.seatsFor(productId, packageId)
}

/** Where a subscription was bought; decides where it is managed. */
enum class BillingStore {
    APP_STORE, PLAY_STORE, WEB, PROMOTIONAL, OTHER;

    /** A store that sells and manages its own subscriptions, where a plan bought there is changed. */
    val hasOwnManagement: Boolean get() = this == APP_STORE || this == PLAY_STORE || this == WEB

    companion object {
        /** From RevenueCat's store names (`app_store`, `PLAY_STORE`, `stripe`, `rc_billing`...). */
        fun fromRevenueCat(name: String?): BillingStore = when (name?.trim()?.lowercase()) {
            "app_store", "mac_app_store" -> APP_STORE
            "play_store" -> PLAY_STORE
            "stripe", "rc_billing", "paddle", "web" -> WEB
            "promotional" -> PROMOTIONAL
            else -> OTHER
        }
    }
}

/** One RevenueCat entitlement of the customer, active or not. */
data class Entitlement(
    val id: String,
    val productId: String? = null,
    /** Null: never expires (lifetime or promotional without end). */
    val expiresAt: Instant? = null,
    val willRenew: Boolean = true,
    val store: BillingStore = BillingStore.OTHER,
    val isSandbox: Boolean = false,
    val hasBillingIssue: Boolean = false
) {
    fun isActiveAt(now: Instant): Boolean = expiresAt == null || expiresAt > now
}

/**
 * What one source knows about a customer: the store SDK on this device, or the RevenueCat
 * webhook's mirror in Firestore (`owners/{uid}/billing/revenuecat`).
 */
data class BillingCustomer(
    /** Whose data this is; the SDK can still hold the previous user's for a moment. */
    val uid: String? = null,
    val entitlements: List<Entitlement> = emptyList(),
    /** Where the store lets the customer manage the subscription, if it says. */
    val managementUrl: String? = null,
    /**
     * Until when each subscription the customer bought lasts, by product id (null: no end), as
     * far as this source knows; [Plans.resolvePlan] reads seats from it while a plan changes.
     */
    val subscriptionEnds: Map<String, Instant?> = emptyMap()
)

/** The signed-in owner's effective plan: the trial, or what the store SDK and the mirror say. */
data class OwnerPlan(
    val tier: PlanTier,
    /** The seats of a bought plan; [PlanLimits.UNLIMITED] on the trial, 0 without a plan. */
    val seats: Int,
    /** When the free months end; null until the trial has started (no workplace yet). */
    val trialEndsAt: Instant?,
    /** The entitlement behind [PlanTier.PREMIUM]; null otherwise. */
    val entitlement: Entitlement?,
    /** False when this build cannot sell plans (no RevenueCat key for the platform). */
    val isBillingConfigured: Boolean,
    val managementUrl: String? = null,
    /**
     * The plan the owner pays for in a store (not a promotional grant) and its seats: what a tier
     * change replaces. Null when nothing is bought, e.g. only a promotional grant.
     */
    val storePlan: PlanEntitlement? = null,
    /** The store this build sells in (Google Play, the App Store, the web). */
    val billingStore: BillingStore = BillingStore.OTHER
) {
    val isPaid: Boolean get() = tier == PlanTier.PREMIUM
    val isTrial: Boolean get() = tier == PlanTier.TRIAL

    /** Whether the owner can work: on the trial or a bought plan (else read-only). */
    val isActive: Boolean get() = tier != PlanTier.NONE || !enforcesLimits

    val limits: PlanLimits get() = Plans.limits(tier, seats)

    /** Whether [limits] apply (see [Plans.ENFORCE_LIMITS_WITHOUT_BILLING]). */
    val enforcesLimits: Boolean get() = isBillingConfigured || Plans.ENFORCE_LIMITS_WITHOUT_BILLING

    companion object {
        /** Someone who has not opened a business yet: the trial starts with the first workplace. */
        fun notStarted(isBillingConfigured: Boolean) =
            OwnerPlan(PlanTier.TRIAL, PlanLimits.UNLIMITED, null, null, isBillingConfigured)
    }
}

/**
 * The owner's plan as every member of a workplace sees it (`workplaces/{id}/plan/access`,
 * written by functions/planAccess.js): whether the workplace is paid for and its seats.
 */
data class WorkplacePlan(
    val tier: PlanTier,
    /** Until when the trial or the bought plan lasts; null: no end. */
    val activeUntil: Instant?,
    val trialEndsAt: Instant?,
    /** [PlanLimits.UNLIMITED] on the trial. */
    val seats: Int,
    /** People with an active engagement in any of the owner's workplaces, the owner not counted. */
    val seatsUsed: Int,
    /**
     * SH-220: how many people the team has past [seats], as the function recorded it
     * (`overSeats`); growing past the plan never blocks, the owner is offered the next tier.
     */
    val overSeats: Int = 0,
    /** SH-220: since when the team is past [seats] (`overSince`); null while it fits. */
    val overSince: Instant? = null
) {
    fun isActiveAt(now: Instant): Boolean = tier != PlanTier.NONE && (activeUntil == null || activeUntil > now)

    /** The tier at [now]: a trial or plan whose time ran out is [PlanTier.NONE] before the function notices. */
    fun tierAt(now: Instant): PlanTier = if (isActiveAt(now)) tier else PlanTier.NONE

    val seatsLeft: Int get() = if (seats == PlanLimits.UNLIMITED) PlanLimits.UNLIMITED else (seats - seatsUsed).coerceAtLeast(0)

    /**
     * SH-220: people past the plan's seats, the larger of what the function recorded and what
     * [seatsUsed] says (the two can differ for a moment after a change); 0 on the trial.
     */
    val overBy: Int get() = if (seats == PlanLimits.UNLIMITED) 0 else maxOf(overSeats, seatsUsed - seats, 0)

    val isOverPlan: Boolean get() = overBy > 0
}

/** What buying an offer does to the owner's running plan (SH-52), so the paywall can say when it applies. */
enum class PlanChange {
    /** No plan bought in a store yet: an ordinary purchase. */
    NEW_PLAN,
    /** The offer is the plan the owner pays for. */
    CURRENT,
    /** More seats: the store switches now and accounts for the unused part of the current period. */
    UPGRADE_NOW,
    /** Fewer seats, or the same seats on another period: the store switches at the next renewal. */
    AT_RENEWAL,
    /** A web plan: changed in RevenueCat's customer portal (more seats now, fewer at the renewal). */
    IN_WEB_PORTAL,
    /** Bought in another store, so changed there; buying here would charge twice. */
    IN_OTHER_STORE;

    companion object {
        /**
         * The change buying [offer] in [thisStore] makes to [running] (the store plan,
         * [OwnerPlan.storePlan]). Product ids match exactly, Google Play's as `subscription:basePlan`.
         */
        fun of(offer: PlanOffer, running: PlanEntitlement?, thisStore: BillingStore): PlanChange = when {
            running == null -> NEW_PLAN
            running.entitlement.productId == offer.productId -> CURRENT
            running.entitlement.store != thisStore -> IN_OTHER_STORE
            thisStore == BillingStore.WEB -> IN_WEB_PORTAL
            offer.seats > running.seats -> UPGRADE_NOW
            else -> AT_RENEWAL
        }
    }
}

/** The result of checking a plan limit before creating something. */
sealed interface PlanCheck {
    data object Allowed : PlanCheck

    /** [tier] [PlanTier.NONE]: no plan at all (the trial ended), else the plan's [limit] is used up. */
    data class LimitReached(val resource: PlanResource, val limit: Int, val tier: PlanTier) : PlanCheck

    /**
     * SH-220: allowed, but the team grows past the plan's [seats] to [seatsUsed]. Growing never
     * blocks: the person is saved and the owner is offered the next tier ([Plans.nextTier]).
     */
    data class OverPlan(val seats: Int, val seatsUsed: Int) : PlanCheck
}

/** A billing failure as a fact; the UI words it (UiText). */
class BillingException(val reason: Reason, detail: String? = null) : Exception(detail ?: reason.name) {
    enum class Reason {
        /** This build has no RevenueCat key for the platform. */
        NOT_CONFIGURED,
        /** The person closed the store sheet: not an error to show. */
        CANCELLED,
        /** The offer is gone from the store/offering (refresh the paywall). */
        OFFER_UNAVAILABLE,
        /** The payment waits for approval (e.g. parental approval, slow card). */
        PAYMENT_PENDING,
        /** Purchases are not allowed on this device/account. */
        NOT_ALLOWED,
        /** No network or the store is unreachable. */
        NETWORK,
        /**
         * The plan can only be changed where it was bought (another store, or Web Billing's
         * portal, which the app opened): not an error, a pointer.
         */
        CHANGE_IN_PORTAL,
        /** Anything else the store reported. */
        STORE_ERROR
    }
}
